import { MatCard, MatCardActions, MatCardContent } from '@angular/material/card';

import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AfterViewInit, Component, DestroyRef, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';

import { BreadCrumbService } from '../../../../@vex/components/breadcrumbs/BreadCrumbService';
import { fadeInUp400ms } from '../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../@vex/animations/stagger.animation';
import { Document, DocumentControllerService } from '@eadsysteme/objectstoreclient-v1';
import { SessionService } from '../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateUtil } from '../../../util/DateUtil';
import dayjs from 'dayjs';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatButton, MatIconButton } from '@angular/material/button';
import { saveAs } from 'file-saver';
import { Contact } from '@eadsysteme/contactclient-v1';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { catchError, finalize } from 'rxjs/operators';
import { SnackbarService } from '../../../services/snackbar.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { MatTooltip } from '@angular/material/tooltip';
import { Icons } from '../../../util/Icons';
import { MatInput } from '@angular/material/input';
import { DelayedSearchDirective } from '../../../directives/delayed-search.directive';
import { NumberUtil } from '../../../util/NumberUtil';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AraPaginatorComponent } from '../../util/ara-paginator/ara-paginator.component';
import { AraSearchSelectComponent } from '../../util/ara-search-select/ara-search-select.component';
import ContentTypeEnum = Document.ContentTypeEnum;

dayjs.extend(customParseFormat);

@Component({
	selector: 'vex-file-list',
	imports: [
		MatCard,
		TranslocoDirective,
		MatCardContent,
		MatTableModule,
		MatPaginatorModule,
		MatSortModule,
		MatIcon,
		MatMenu,
		MatMenuTrigger,
		MatIconButton,
		MatMenuItem,
		AraPaginatorComponent,
		MatProgressSpinner,
		MatCardActions,
		MatCheckboxModule,
		MatTooltip,
		MatButton,
		MatInput,
		DelayedSearchDirective,
		AraSearchSelectComponent,
	],
	templateUrl: './file-list.component.html',
	styleUrl: './file-list.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class FileListComponent implements OnInit, AfterViewInit {
	protected breadCrumbService = inject(BreadCrumbService);
	private documentService = inject(DocumentControllerService);
	protected icons = inject(Icons);
	private dialog = inject(MatDialog);
	private sessionService = inject(SessionService);
	private snackbar = inject(SnackbarService);
	private translocoService = inject(TranslocoService);
	private dateUtil = inject(DateUtil);
	private numberUtil = inject(NumberUtil);

	private destroyRef = inject(DestroyRef);

	displayedColumns: string[] = [
		'select',
		'reNumber',
		'tuNumber',
		'propertyManagement',
		'fileName',
		'fileType',
		'fileSize',
		'uploadDate',
		'deletionDate',
		'actions',
	];

	displayedColumnFilters: string[] = this.displayedColumns.filter((c) => c !== 'actions').map((c) => c + 'Filter');

	dataSource: MatTableDataSource<File> = new MatTableDataSource();
	propertyManagementContacts = signal<Contact[]>([]);

	selection = new SelectionModel<File>(true, []);
	totalSelectedSize = 0;
	readonly MAX_DOWNLOAD_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB

	multiDownloadEnabled = false;
	multiDownloadMaxFileSizeExceeded = false;

	searchFilterSubject: Subject<string> = new Subject<string>();
	searchFilter$: Observable<string> = this.searchFilterSubject.asObservable();

	@ViewChild(AraPaginatorComponent)
	set paginatorComponent(paginatorComponent: AraPaginatorComponent) {
		if (paginatorComponent) {
			this.dataSource.paginator = paginatorComponent.paginator();
		}
	}

	@ViewChild(MatSort)
	set sort(sort: MatSort) {
		this.dataSource.sort = sort;
	}

	pageSizeOptions = [10, 20, 50];
	pageSize = 10;
	pageIndex = 0;

	isLoading = signal(false);

	private unfilteredData: File[] = [];
	private activeFilters: { [key: string]: any[] } = {};

	clearFilter: Subject<boolean> = new Subject<boolean>();
	preSetCheckedDataReNumber: Subject<any[]> = new Subject<any[]>();
	preSetCheckedDataTuNumber: Subject<any[]> = new Subject<any[]>();
	preSetCheckedDataPropertyManagement: Subject<any[]> = new Subject<any[]>();
	preSetCheckedDataFileType: Subject<any[]> = new Subject<any[]>();

	ngOnInit() {
		this.propertyManagementContacts.set(this.sessionService.propertyManagementContacts());
		this.searchFilterSubject.next('');
		this.selection.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.totalSelectedSize = this.selection.selected.reduce((acc, file) => acc + (file.fileSize || 0), 0);
			this.multiDownloadMaxFileSizeExceeded = this.totalSelectedSize > this.MAX_DOWNLOAD_SIZE_BYTES;
			this.multiDownloadEnabled = !this.selection.isEmpty() && !this.multiDownloadMaxFileSizeExceeded;
		});
	}

	async ngAfterViewInit() {
		this.searchFilter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((searchFilter) => {
			this.dataSource.filter = searchFilter;
			if (this.dataSource.paginator) {
				this.dataSource.paginator.firstPage();
			}
		});
		this.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'uploadDate':
				case 'deletionDate': {
					const dateStr = item[property] as string;
					if (!dateStr) {
						return 0;
					}
					const date = dayjs(dateStr, 'DD.MM.YYYY');
					return date.isValid() ? date.valueOf() : 0;
				}
				case 'tuNumber': {
					let tuNumber = item[property] as string;
					if (!tuNumber?.length) {
						tuNumber = '0/0';
					}
					const parts = tuNumber.split('/').map((s) => Number(s));
					if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
						// 10/2 => 100002, 1/0 => 10000, 1/1 => 10001, 2/1 => 20001
						return parts[0] * 10000 + parts[1];
					}
					return 0;
				}
				case 'propertyManagement':
					return this.getPropertyManagementName(item[property]);
				default:
					return item[property];
			}
		};
	}

	refreshList(): void {
		this.isLoading.set(true);
		const selectedIds = this.selection.selected.map((s) => s.documentId);
		this.loadDocuments$(this.propertyManagementContacts())
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.isLoading.set(false))
			)
			.subscribe({
				next: (documentsResponses: Document[][]) => {
					const files = documentsResponses.flatMap((docs) => this.mapDocuments(docs));
					this.dataSource.data = this.unfilteredData = files;
					const newSelectedRows = files.filter((row) => selectedIds.includes(row.documentId));
					this.selection.select(...newSelectedRows);
					this.clearAllFilters();
				},
				error: () => {
					this.snackbar.error(this.translocoService.translate('documentTransfer.errorLoadingDocuments'));
					this.dataSource.data = [];
				},
			});
	}

	filterData(column: string): any[] {
		if (!this.unfilteredData) {
			return [];
		}
		const uniqueValues = new Set(
			this.unfilteredData
				.map((item) => {
					if (column === 'propertyManagement') {
						return this.getPropertyManagementName(item[column]);
					}
					return item[column];
				})
				.filter((value) => value != null)
		);

		return Array.from(uniqueValues).sort();
	}

	reloadDataWithFilter(column: string, filters: any[]) {
		if (filters && filters.length > 0) {
			this.activeFilters[column] = filters;
		} else {
			delete this.activeFilters[column];
		}
		this.applyFilters();
	}

	private applyFilters() {
		let filteredData = [...this.unfilteredData];

		Object.keys(this.activeFilters).forEach((column) => {
			const filterValues = this.activeFilters[column];
			if (filterValues.length > 0) {
				filteredData = filteredData.filter((item) => {
					if (column === 'propertyManagement') {
						return filterValues.includes(this.getPropertyManagementName(item[column]));
					}
					return filterValues.includes(item[column]);
				});
			}
		});

		this.dataSource.data = filteredData;

		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	clearAllFilters(): void {
		this.activeFilters = {};
		this.searchFilterSubject.next('');
		this.dataSource.data = [...this.unfilteredData];
		this.clearFilter.next(true);
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	isAllSelected() {
		if (this.dataSource.filteredData.length === 0) {
			return false;
		}
		return this.dataSource.filteredData.every((row) => this.selection.isSelected(row));
	}

	masterToggle() {
		if (this.isAllSelected()) {
			this.selection.clear();
			return;
		}

		this.selection.select(...this.dataSource.filteredData);
	}

	checkboxLabel(row?: File): string {
		if (!row) {
			return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
		}
		return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.fileName}`;
	}

	private loadDocuments$(propertyManagementContacts: Contact[]): Observable<Document[][]> {
		if (!propertyManagementContacts || propertyManagementContacts.length === 0) {
			return of([]);
		}
		const requests: Observable<Document[]>[] = propertyManagementContacts.map((contact) => {
			if (contact?.area && contact?.accountNumber) {
				return this.documentService.getAllDocumentsOfPropertyManagement(contact.area, contact.accountNumber, false).pipe(
					catchError(() => {
						this.snackbar.error(
							this.translocoService.translate('documentTransfer.errorLoadingDocumentsForPropertyManagement', {
								pm: this.getPropertyManagementName(contact),
							})
						);
						return of([]);
					})
				);
			} else {
				return of([]);
			}
		});

		return forkJoin(requests);
	}

	private mapDocuments(documentsOfPm: Array<Document>) {
		return documentsOfPm.map((document) => {
			const uploadDate = document.properties.find((property) => property.name === 'uploadDate')?.value ?? '';
			const fileName = document.name ?? '';
			const fileType = document.contentType ?? '';
			const automaticDeletionDays = Number(document.properties.find((property) => property.name === 'automaticDeletionInDays')?.value ?? 0);
			const deletionDateString = automaticDeletionDays
				? this.dateUtil.setDateFormatByActiveLang(dayjs(uploadDate).add(Number(automaticDeletionDays), 'days'), '')
				: this.translocoService.translate('documentTransfer.archive');
			const propertyManagementContact = this.propertyManagementContacts().find((pm) => pm.accountNumber === document.categoryName);

			return {
				deletionDate: deletionDateString,
				documentId: document.id,
				fileName: fileName,
				fileSize: document.size ?? 0,
				fileType: fileType,
				lastDownloadDate: '',
				propertyManagement: propertyManagementContact,
				reNumber: document.properties.find((property) => property.name === 'reNumber')?.value ?? '',
				tuNumber: document.properties.find((property) => property.name === 'tuNumber')?.value ?? '',
				uploadDate: this.dateUtil.setDateFormatByActiveLang(dayjs(uploadDate), ''),
				uploadUser: '',
			};
		});
	}

	onDownloadClick(row: File) {
		this.documentService
			.getDocumentContent(this.sessionService.effectiveArea, row.documentId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (blob) => {
					saveAs(blob, this.getFileNameWithExtension(row));
				},
				error: () => {
					this.snackbar.error(
						this.translocoService.translate('documentTransfer.errorDownloadingFile', { fileName: this.getFileNameWithExtension(row) })
					);
				},
			});
	}

	onPreviewClick(row: File) {
		this.documentService
			.getDocumentContent(this.sessionService.effectiveArea, row.documentId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (blob) => {
					const blobURL = URL.createObjectURL(blob);
					window.open(blobURL);
				},
				error: () => {
					this.snackbar.error(
						this.translocoService.translate('documentTransfer.errorDownloadingFile', { fileName: this.getFileNameWithExtension(row) })
					);
				},
			});
	}

	onDeleteClick(row: File) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('documentTransfer.confirmDeleteFile', {
			fileName: this.getFileNameWithExtension(row),
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.documentService
					.deleteDocument(this.sessionService.effectiveArea, row.documentId)
					.pipe(
						takeUntilDestroyed(this.destroyRef),
						finalize(() => this.refreshList())
					)
					.subscribe({
						next: () => {
							this.snackbar.success(
								this.translocoService.translate('documentTransfer.fileDeleted', { fileName: this.getFileNameWithExtension(row) })
							);
						},
						error: () => {
							this.snackbar.error(
								this.translocoService.translate('documentTransfer.errorDeletingFile', { fileName: this.getFileNameWithExtension(row) })
							);
						},
					});
			}
		});
	}

	multiDownload() {
		this.documentService
			.getDocumentsAsZip(
				this.sessionService.effectiveArea,
				this.selection.selected.map((value) => value.documentId)
			)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (blob) => {
					const filename = dayjs().format('YYYY-MM-DD_HH-mm-ss') + '.zip';
					saveAs(blob, filename);
				},
			});
	}

	getPropertyManagementName(propertyManagement: Contact): string {
		if (!propertyManagement) {
			return '';
		}
		return [propertyManagement.firstName, propertyManagement.name].join(' ');
	}

	getFileNameWithExtension(row: File): string {
		if (!row) {
			return '';
		}

		let fileName = row.fileName;
		if (row.fileType !== ContentTypeEnum.UNDEFINED && !fileName.toLowerCase().endsWith(row.fileType.toLowerCase())) {
			fileName += '.' + row.fileType.toLowerCase();
		}
		return fileName;
	}

	getByteFileSizeString(fileSize: number): string {
		let size = fileSize ?? 0;
		if (size < 1024) {
			return size + ' B';
		}
		size /= 1024;
		if (size < 1024) {
			return this.numberUtil.scaleAndReplaceDot(size, 1) + ' KB';
		}
		size /= 1024;
		return this.numberUtil.scaleAndReplaceDot(size, 1) + ' MB';
	}

	protected readonly ContentTypeEnum = ContentTypeEnum;
}

export interface File {
	deletionDate: string;
	documentId: string;
	fileName: string;
	fileSize: number;
	fileType: string;
	lastDownloadDate: string;
	propertyManagement: Contact;
	reNumber: string;
	tuNumber: string;
	uploadDate: string;
}
